import { useAuth0 } from "@auth0/auth0-react";
import { sum } from "lodash";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  BarChartOutlined,
  Construction,
  Delete,
  Edit,
  Output,
  Share,
} from "@mui/icons-material";
import {
  Alert,
  Button,
  Chip,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import { UploadSkeleton } from "./UploadSkeleton";
import {
  buildUpload,
  getInsight,
  saveAudience,
  shareAudience,
  updateAudienceName,
  upload,
  validateFile,
} from "@/api";
import {
  AudienceSegmentsSize,
  AudienceSize,
  ErrorPanel,
  FileUpload,
  HelpBox,
  HelpText,
  UploadInfo,
  useActivePage,
} from "@/components/atoms";
import { PageContainer } from "@/components/containers";
import { AudienceMiniStats, SelectableMenuCard } from "@/components/molecules";
import { useAudienceContext, useDialogs } from "@/context";
import { useAudience } from "@/hooks";
import {
  AudienceSegment,
  AudienceSummary,
  Breadcrumb,
  ValidFile,
} from "@/types";
import {
  checkAudienceSize,
  getAudienceBreadcrumb,
  uploadModeOptions,
} from "@/utils";

const UploadPage: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { state, dispatch } = useAudienceContext();
  const navigate = useNavigate();
  const { openDialog, closeDialog } = useDialogs();

  let { audienceId } = useParams<{ audienceId: string }>();
  audienceId = audienceId === "new" ? undefined : audienceId;
  const {
    audience,
    loading: audienceLoading,
    error: audienceError,
  } = useAudience(audienceId, "upload");
  const uploadFile = audience?.upload_file;
  const uploadMode = audience?.upload_mode;
  const audienceSharedToUser = audience?.shared;
  const readOnly = audience?.shared;

  const activePage = useActivePage();
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([]);

  const pageLoading = audienceLoading;

  const [validFile, setValidFile] = useState<ValidFile | null>(null);
  const [checkingFile, setCheckingFile] = useState<boolean>(false);

  const [buildLoading, setBuildLoading] = useState<boolean>(false);
  const [buildError, setBuildError] = useState<string | null>(null);
  const [insightLoading, setInsightLoading] = useState<boolean>(false);
  const [insightError, setInsightError] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);

  const readyToUpload = uploadMode && uploadFile && uploadFile instanceof File;
  const readyToBuild = readyToUpload && validFile?.format;

  useEffect(() => {
    setBreadcrumbs(
      getAudienceBreadcrumb(activePage, audience, audienceLoading),
    );
  }, [activePage, audience, audienceLoading]);

  useEffect(() => {
    state.change && setBuildError(null);
  }, [state.change]);

  const extractSegments = (summary: AudienceSummary[]): AudienceSegment[] => {
    return summary.map((x, index) => ({
      segment_id: index + 1,
      segment_name: x.segment ?? `Segment ${index + 1}`,
    }));
  };

  const handleChangeMode = (mode: string) => {
    dispatch({ type: "CHANGE_UPLOAD_MODE", payload: mode });
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (files) {
      dispatch({ type: "ADD_UPLOAD_FILE", payload: files[0] });
    }
  };

  useEffect(() => {
    async function checkFile(file: File, mode: string) {
      setValidFile(null);
      setCheckingFile(true);

      const token = await getAccessTokenSilently();
      const { data, error } = await validateFile(token, file, mode);

      if (data) {
        setValidFile(data);
      }
      if (error) {
        setValidFile({ format: false, segment: false, error: true });
      }

      setCheckingFile(false);
    }

    readyToUpload && checkFile(uploadFile, uploadMode);
  }, [getAccessTokenSilently, readyToUpload, uploadFile, uploadMode]);

  const handleBuildAudience = async () => {
    const token = await getAccessTokenSilently();
    setBuildError(null);
    setBuildLoading(true);

    if (readyToBuild) {
      const { data, error } = await buildUpload(token, uploadFile, uploadMode);

      if (data) {
        if (checkAudienceSize(data.audience_size[0])) {
          dispatch({ type: "BUILD_AUDIENCE", payload: data });
          if (data.audience_size.length > 1) {
            const audienceSegments = extractSegments(data.audience_size);
            dispatch({ type: "ADD_SEGMENTS", payload: audienceSegments });
          }
        } else {
          setBuildError(
            "No valid geos were found in your file, please check the file contents and try again",
          );
        }
      }

      if (error) {
        setBuildError(error.message);
      }
    }

    setBuildLoading(false);
  };

  const handleAudienceInsight = () => {

    if (!audience) return;

    if (audience.audience_id && audience.audience_id !== "") {
      // Existing audience - if change show warning and rebuild otherwise go to insights
      if (state.change) {
        openDialog({
          type: 'confirm',
          props: {
            message:
              "Proceeding will mean that your saved audience will get overwritten. Create a new audience if you don't want this to happen.",
            onConfirm: handleUpdateAudience,
          },
        });
      } else {
        navigate("insight");
      }
    } else {
      // New audience - show save audience dialog then go to insights
      openDialog({
        type: 'createAudience',
        props: {
          onConfirm: handleUpdateAudience,
        },
      });
    }
  };

  const handleAudienceExport = () => {
    navigate("export");
  };

  const handleUpdateAudience = async (audience_name?: string) => {
    const token = await getAccessTokenSilently();
    closeDialog();
    setInsightError(null);
    setInsightLoading(true);

    if (audience && readyToUpload) {
      // Upload the file to create an audience
      const uploadData = await upload(token, uploadFile, uploadMode);

      if (uploadData.data) {
        // Prepare the audience data to save
        let newAudienceName = audience_name ?? audience.audience_name;
        let newUploadFile = { name: uploadFile.name };
        let updatedAudience = {
          ...audience,
          audience_id: uploadData.data.audience_id,
          audience_name: newAudienceName,
          upload_file: newUploadFile,
        };

        // Save the audience - this builds the audience tables too
        const audienceData = await saveAudience(token, updatedAudience, true);

        if (audienceData.data) {
          const newAudience = audienceData.data.filter(
            (audience) =>
              audience.audience_name === updatedAudience.audience_name,
          )[0];
          dispatch({
            type: "UPDATE_AUDIENCE",
            payload: {
              audiences: audienceData.data,
              currentAudience: newAudience,
            },
          });

          const insight = await getInsight(
            token,
            newAudience.audience_id,
            true,
          );

          if (insight.data) {
            dispatch({ type: "CREATE_INSIGHT", payload: { ...insight.data } });
            navigate(`../${newAudience.audience_id}/insight`, {
              relative: "path",
            });
          }

          if (insight.error) {
            setInsightError(insight.error.message);
          }
        }

        if (audienceData.error) {
          setInsightError(audienceData.error.message);
        }
      }

      if (uploadData.error) {
        setInsightError(uploadData.error.message);
      }
    }

    setInsightLoading(false);
  };

  const handleUpdateAudienceName = async (
    audienceName: string,
    projectName: string | null,
  ) => {
    const token = await getAccessTokenSilently();

    if (!audienceId) return;

    const { data, error } = await updateAudienceName(
      token,
      audienceId,
      audienceName,
      projectName,
    );

    if (data) {
      dispatch({
        type: "CHANGE_NAME",
        payload: { audienceId, audienceName, projectName },
      });
      closeDialog();
    }

    if (error) {
      closeDialog();
      setUpdateError(error.message);
    }
  };

  const handleShareAudience = async (ids: number[]) => {
    const accessToken = await getAccessTokenSilently();

    const { data, error } = await shareAudience(
      accessToken,
      audience.audience_id,
      ids,
    );

    if (data) {
      dispatch({
        type: "SHARE_AUDIENCE",
        payload: { audienceId: audience.audience_id, userIds: ids },
      });
      closeDialog();
    }

    if (error) {
      setUpdateError(error.message);
      closeDialog();
    }
  };

  const summariseAudience = (summary: AudienceSummary[]) => {
    return {
      num_postcodes: sum(summary.map((x) => x.num_postcodes)),
      num_sectors: sum(summary.map((x) => x.num_sectors)),
      num_households: sum(summary.map((x) => x.num_households)),
      population_count: sum(summary.map((x) => x.population_count)),
    };
  };

  const actionButtons = (
    <Stack direction="row" spacing={2}>
      <Button
        variant="contained"
        size="small"
        color="error"
        disableElevation
        startIcon={<Delete />}
        disabled={
          pageLoading ||
          buildLoading ||
          insightLoading ||
          audienceId === undefined ||
          audienceSharedToUser
        }
        onClick={() =>
          openDialog({
            type: 'deleteAudience',
            props: {
              audience,
              redirect: '/upload',
            },
          })
        }
      >
        Delete
      </Button>
      {!audienceSharedToUser && (
        <Button
          variant="contained"
          size="small"
          disableElevation
          startIcon={<Share />}
          disabled={
            pageLoading ||
            buildLoading ||
            insightLoading ||
            audienceId === undefined
          }
          onClick={() =>
            openDialog({
              type: 'shareAudience',
              props: {
                selectedAudience: audience,
                onShare: async (ids: number[]) => {
                  await handleShareAudience(ids);
                },
              },
            })
          }
        >
          Share
        </Button>
      )}
      <Button
        variant="contained"
        size="small"
        disableElevation
        startIcon={<Construction />}
        loading={buildLoading}
        disabled={
          state.built ||
          !readyToBuild ||
          !validFile?.format ||
          audienceSharedToUser
        }
        onClick={handleBuildAudience}
      >
        Build
      </Button>
      {audience && audience.audience_size && (
        <>
          <Button
            variant="contained"
            size="small"
            disableElevation
            startIcon={<BarChartOutlined />}
            loading={insightLoading}
            disabled={!state.built}
            onClick={handleAudienceInsight}
          >
            Insight
          </Button>
          <Button
            variant="contained"
            size="small"
            disableElevation
            startIcon={<Output />}
            disabled={!state.built || insightLoading || buildLoading}
            onClick={handleAudienceExport}
          >
            Export
          </Button>
        </>
      )}
    </Stack>
  );

  return (
    <PageContainer
      breadcrumbs={audienceError ? undefined : breadcrumbs}
      actions={audienceError ? undefined : actionButtons}
      loading={buildLoading || insightLoading}
      loadingText={
        buildLoading ? "Building Upload" : "Calculating Audience Insight"
      }
    >
      {pageLoading ? (
        <UploadSkeleton />
      ) : (
        <Grid container spacing={2} sx={{ pb: 4 }}>
          <Grid container size={12}>
            <ErrorPanel
              audienceError={audienceError}
              audienceUpdateError={updateError}
              buildError={buildError}
              insightError={insightError}
            />
          </Grid>
          {audience && (
            <>
              {/* Audience Name and Summary */}
              <Grid
                size={12}
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="h6">
                    {audience.audience_name || "New Audience"}
                  </Typography>
                  {audience.project_name && (
                    <Chip size="small" label={audience.project_name} />
                  )}
                  {audienceId && !audienceSharedToUser && (
                    <HelpBox title="Edit Audience/Project Name">
                      <IconButton
                        size="small"
                        onClick={() =>
                          openDialog({
                            type: 'editAudience',
                            props: {
                              audienceName: audience.audience_name,
                              projectName: audience.project_name,
                              onConfirm: async (
                                audienceName: string,
                                projectName: string | null
                              ) => {
                                await handleUpdateAudienceName(
                                  audienceName,
                                  projectName
                                );
                              },
                            },
                          })
                        }
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </HelpBox>
                  )}
                </Stack>
                {audience.audience_size && (
                  <AudienceMiniStats
                    audienceSize={summariseAudience(audience.audience_size)}
                  />
                )}
              </Grid>

              {readOnly && (
                <Grid size={12}>
                  <Alert variant="filled" severity="info">
                    You are viewing an upload that has been shared with you and
                    therefore any edit functionality has been disabled. You may
                    still view the insights for and export this upload. We will
                    soon be adding the ability to clone this upload so that you
                    can make changes.
                  </Alert>
                </Grid>
              )}

              {/* Heading and Mode Select */}
              {!readOnly && (
                <>
                  <Grid size={12}>
                    <HelpText
                      text="Select Mode"
                      helpText="Define the type of upload audience you are creating"
                      variant="body1"
                    />
                  </Grid>
                  <Grid container size={12} spacing={2}>
                    {uploadModeOptions.map((option, index) => (
                      <Grid key={index} pb={2}>
                        <SelectableMenuCard
                          title={option.label}
                          text={option.description}
                          onClick={() => handleChangeMode(option.value)}
                          icon={option.icon}
                          disabled={option.disabled}
                          selected={audience.upload_mode === option.value}
                          height="152px"
                        />
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}

              {/* Mode Options */}
              {audience.upload_mode && (
                <>
                  <Grid size={12}>
                    <HelpText
                      text="Select File"
                      helpText="Select the file you wish to upload, making sure that it adheres to the specific upload format"
                      variant="body1"
                    />
                  </Grid>
                  {!readOnly && (
                    <Grid size={12}>
                      <UploadInfo mode={audience.upload_mode} />
                    </Grid>
                  )}
                  <Grid size={12}>
                    <FileUpload
                      currentFile={audience.upload_file}
                      validFile={validFile}
                      loading={checkingFile}
                      onChange={handleFileUpload}
                      allowSegments={true}
                      disabled={readOnly}
                    />
                  </Grid>
                </>
              )}

              {/* Audience Size */}
              {audience.audience_size && (
                <>
                  <Grid size={12} mt={2}>
                    <HelpText
                      text="Audience Size"
                      helpText="The number of postcodes in your upload, and how many households/people this represents"
                      variant="body1"
                    />
                  </Grid>
                  {audience.audience_size.length > 1 ? (
                    <AudienceSegmentsSize data={audience.audience_size} />
                  ) : (
                    <AudienceSize data={audience.audience_size[0]} />
                  )}
                </>
              )}
            </>
          )}
        </Grid>
      )}
    </PageContainer>
  );
};

export default UploadPage;
